import React, { useState, useEffect, useRef } from 'react';

const TimeInput = ({ hour, minute, onHourChange, onMinuteChange }) => {
    const [hourInput, setHourInput] = useState('');
    const [minuteInput, setMinuteInput] = useState('');
    const [period, setPeriod] = useState('AM');

    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const isTypingRef = useRef(false);
    const isPeriodChangingRef = useRef(false);

    // Initialize inputs based on current hour/minute
    useEffect(() => {
        // Don't override while user is typing or changing period
        if (isTypingRef.current || isPeriodChangingRef.current) {
            console.log('[TimeInput] useEffect skipped - user is interacting');
            return;
        }

        let displayHour = hour;
        let displayPeriod = 'AM';

        if (hour === 0) {
            displayHour = 12;
            displayPeriod = 'AM';
        } else if (hour === 12) {
            displayHour = 12;
            displayPeriod = 'PM';
        } else if (hour > 12) {
            displayHour = hour - 12;
            displayPeriod = 'PM';
        }

        setHourInput(displayHour.toString());
        setMinuteInput(minute.toString().padStart(2, '0'));
        setPeriod(displayPeriod);
    }, [hour, minute]);

    const handleHourChange = (e) => {
        isTypingRef.current = true;
        const value = e.target.value.replace(/\D/g, ''); // Remove non-digits

        // Allow any numeric input up to 2 digits
        if (value.length <= 2) {
            setHourInput(value);

            // If 2 digits entered, auto-focus to minute field
            if (value.length === 2) {
                setTimeout(() => {
                    minuteRef.current?.focus();
                    // Don't reset isTypingRef here - let blur handle it
                }, 0);
            }
        }
    };

    const handleMinuteChange = (e) => {
        isTypingRef.current = true;
        const value = e.target.value.replace(/\D/g, ''); // Remove non-digits

        // Allow any numeric input up to 2 digits
        if (value.length <= 2) {
            setMinuteInput(value);
        }
    };

    const setPeriodTo = (newPeriod) => {
        console.log('[TimeInput] setPeriodTo called:', {
            currentPeriod: period,
            newPeriod,
            isTyping: isTypingRef.current,
            hourInput,
            minuteInput
        });

        if (period === newPeriod) {
            console.log('[TimeInput] Period already set to', newPeriod);
            return; // Already set, do nothing
        }

        isPeriodChangingRef.current = true; // Prevent useEffect from overriding
        isTypingRef.current = false; // Allow state to update

        setPeriod(newPeriod);
        console.log('[TimeInput] Period updated to', newPeriod);

        // Immediately update parent when period changes
        const hourValue = parseInt(hourInput) || 12;
        const minuteValue = parseInt(minuteInput) || 0;
        console.log('[TimeInput] Calling updateTime with:', { hourValue, minuteValue, newPeriod });
        updateTime(hourValue, minuteValue, newPeriod);

        // Reset flag after a short delay to allow update to complete
        setTimeout(() => {
            isPeriodChangingRef.current = false;
            console.log('[TimeInput] Period change complete');
        }, 100);
    };

    const updateTime = (displayHour, displayMinute, displayPeriod) => {
        let hour24 = displayHour;

        if (displayPeriod === 'PM' && displayHour !== 12) {
            hour24 = displayHour + 12;
        } else if (displayPeriod === 'AM' && displayHour === 12) {
            hour24 = 0;
        }

        onHourChange(hour24);
        onMinuteChange(displayMinute);
    };

    const handleHourBlur = () => {
        isTypingRef.current = false;
        let value = hourInput;

        // If empty, default to 12
        if (value === '') {
            value = '12';
            setHourInput('12');
            // Don't call updateTime - let the existing state persist
            return;
        }

        // Validate and clamp to valid range
        let numValue = parseInt(value);
        if (numValue < 1) {
            numValue = 1;
        } else if (numValue > 12) {
            numValue = 12;
        }

        setHourInput(numValue.toString());

        // Update parent with new time
        updateTime(numValue, parseInt(minuteInput) || 0, period);
    };

    const handleMinuteBlur = () => {
        isTypingRef.current = false;
        let value = minuteInput;

        // If empty, default to 00
        if (value === '') {
            value = '0';
        }

        // Validate and clamp to valid range
        let numValue = parseInt(value);
        if (numValue < 0) {
            numValue = 0;
        } else if (numValue > 59) {
            numValue = 59;
        }

        // Pad to 2 digits on blur
        const paddedValue = numValue.toString().padStart(2, '0');
        setMinuteInput(paddedValue);

        // Update parent with new time
        updateTime(parseInt(hourInput) || 12, numValue, period);
    };

    const handleFocus = (e) => {
        isTypingRef.current = true;
        // Select all text on focus for easy overwrite
        e.target.select();
    };

    return (
        <div className="space-y-3">
            <div className="text-xs font-medium text-gray-500 mb-2">Select Time</div>

            <div className="flex items-center justify-center gap-3">
                {/* Hour Input */}
                <input
                    ref={hourRef}
                    type="text"
                    inputMode="numeric"
                    value={hourInput}
                    onChange={handleHourChange}
                    onBlur={handleHourBlur}
                    onFocus={handleFocus}
                    placeholder="12"
                    maxLength={2}
                    className="w-16 px-3 py-2 text-center text-lg font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />

                {/* Separator */}
                <span className="text-2xl font-bold text-gray-600">:</span>

                {/* Minute Input */}
                <input
                    ref={minuteRef}
                    type="text"
                    inputMode="numeric"
                    value={minuteInput}
                    onChange={handleMinuteChange}
                    onBlur={handleMinuteBlur}
                    onFocus={handleFocus}
                    placeholder="00"
                    maxLength={2}
                    className="w-16 px-3 py-2 text-center text-lg font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />

                {/* AM/PM Toggle */}
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setPeriodTo('AM')}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${period === 'AM'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        AM
                    </button>
                    <button
                        type="button"
                        onClick={() => setPeriodTo('PM')}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${period === 'PM'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        PM
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TimeInput;
